<template src="./Balance.html" />

<script>
import { computed } from "vue"
import { Money } from "v-money"
import VueSlider from "vue-slider-component"
import ButtonSpinner from "vue-button-spinner"
import "vue-slider-component/theme/antd.css"

import { useStore } from "@/store/store.js"
import { FilesHelper } from "@/helpers/FilesHelper"
import { optionsSlider } from "@/constants/slider.js"
import { monetaryConfig } from "@/constants/money.js"
import { bankOperations, needsAgDigit, needsOpCode, unneedsAcDigit } from "@/constants/banks"
import mixins from "@/Mixins.js"

export default {
  name: "Balance",

  mixins: [mixins],

  components: {
    Money,
    VueSlider,
    ButtonSpinner,
  },

  data () {
    return {
      activeAlert: true,
      banksList: [],
      financialData: {},
      balance: {
        toWithdraw: 0,
        available: 0,
        disabledRescue: true,
        withdrawTax: 0,
      },
      form: {
        isLegalPerson: "",
        companyCnpj: "",
        companyName: "",
        responsibleName: "",
        responsibleCpf: "",
        zipcode: "",
        street: "",
        city: "",
        state: "",
        bank: "",
        bankAgency: "",
        bankAgencyDigit: "",
        bankOperation: "",
        bankAccount: "",
        bankAccountDigit: "",
        isVerifiedAccount: "",
        filesSelfie: null,
        filesAddress: null,
        filesIdentification: null,
        status: "",
        socialContract: null,
        balanceSheet: null,
      },
      invalidField: {
        isLegalPerson: false,
        companyCnpj: false,
        companyName: false,
        responsibleName: false,
        responsibleCpf: false,
        zipcode: false,
        street: false,
        city: false,
        state: false,
        bank: false,
        bankAgency: false,
        bankAgencyDigit: false,
        bankOperation: false,
        bankAccount: false,
        bankAccountDigit: false,
        isVerifiedAccount: false,
        filesSelfie: false,
        filesAddress: false,
        filesIdentification: false,
        socialContract: false,
        balanceSheet: false,
      },
      isDisabledAddressStreet: true,
      isDisabledAddressCity: true,
      isDisabledAddressState: true,
      isEditFormShow: false,
    }
  },

  computed: {
    computedBalanceAvailable () {
      return (this.financialData.balance_avaliable_for_withdraw || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    },

    computedIsVerifiedAccount() {
      return this.financialData && this.financialData.is_verified
        ? this.financialData.is_verified
        : false
    },

    computedBalanceToWithdraw () {
      return (this.balance.toWithdraw || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    },

    computedBalanceWithdrawTax () {
      return (this.balance.withdrawTax || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    },

    computedBank () {
      return this.financialData && this.financialData.details
        ? this.financialData.details.bank
        : ""
    },

    computedBankAgency () {
      return this.financialData && this.financialData.details
        ? this.financialData.details.bank_agency
        : ""
    },

    computedBankAccount () {
      return this.financialData && this.financialData.details
        ? this.financialData.details.bank_account
        : ""
    },

    maskBankAgency () {
      return this.form.bank && this.form.bank.name ? this.form.bank.maskAgency : "####"
    },

    maskBankAccount () {
      return this.form.bank && this.form.bank.name ? this.form.bank.maskAccount : "#########"
    },

    isButtonWithdrawDisabled () {
      return this.isEditFormShow || (this.financialData.balance_avaliable_for_withdraw || 0) <= 0
    },

    isBankAgencyDigitRequired () {
      return this.form.bank && this.form.bank.name && needsAgDigit.includes(this.form.bank.name)
    },

    isBankAccountDigitRequired () {
      return this.form.bank && this.form.bank.name && !unneedsAcDigit.includes(this.form.bank.name)
    },

    isBankOperationRequired () {
      return this.form.bank && this.form.bank.name && needsOpCode.includes(this.form.bank.name)
    },

    computedFinancialDetails () {
      const data = {
        address: this.form.street,
        bank: this.form.bank.name,
        bank_account: this.form.bankAccount,
        bank_agency: this.form.bankAgency,
        cep: this.getOnlyNumbers(this.form.zipcode),
        city: this.form.city,
        cpf: this.getOnlyNumbers(this.form.responsibleCpf || ""),
        is_legal_person: this.form.isLegalPerson || false,
        resp_name: this.form.responsibleName || "",
        resp_cpf: this.form.isLegalPerson ? this.getOnlyNumbers(this.form.responsibleCpf) : null,
        state: (this.form.state || "").toUpperCase(),
      }

      if (needsAgDigit.includes(this.form.bank.name)) {
        data.bank_agency = `${data.bank_agency}-${this.form.bankAgencyDigit}`
      }

      if (!unneedsAcDigit.includes(this.form.bank.name)) {
        data.bank_account = `${data.bank_account}-${this.form.bankAccountDigit}`
      }

      if (needsOpCode.includes(this.form.bank.name)) {
        data.bank_account = `${this.form.bankOperation.code}${data.bank_account}`
      }

      if (this.form.isLegalPerson) {
        data.company_name = this.form.companyName || ""
        data.cnpj = this.getOnlyNumbers(this.form.companyCnpj || "")
      }

      return data
    },
  },

  setup () {
    const store = useStore()

    return {
      optionsSlider,
      bankOperations,
      monetaryConfig,
      filooServer: computed(() => store.state.filooServer),
      isMobile: computed(() => store.getters.isMobile),
      filesConfig: {
        selfie: { name: "filesSelfie", extensions: ["png", "jpg", "jpeg"], minRes: 300, maxRes: 2000 },
        address: { name: "filesAddress", extensions: ["png", "jpg", "jpeg", "pdf"], size: 10240, minRes: 300 },
        identification: { name: "filesIdentification", extensions: ["png", "jpg", "jpeg", "pdf"], size: 10240, minRes: 300 },
      },
      getAddressByZipcode: (cep) => store.dispatch("getAddressByZipcode", cep),
    }
  },

  async mounted () {
    this.$vs.loading()
    await this.init()
  },

  watch: {
    "form.bank" (val, old) {
      this.invalidField.bank = false

      if (val && old && val.name !== old.name) {
        const fields = ["bankAgency", "bankAgencyDigit", "bankOperation", "bankAccount", "bankAccountDigit"]

        fields.forEach((field) => this.form[field] = "" && this.clearInvalidField(field))
      }
    },
  },

  methods: {
    async init () {
      await this.resetBalanceForm()
      await this.getBanks()
      await this.getAccountDetails()
      await this.getWithdrawTax()
      await this.getPersonalData()
    },

    resetBalanceForm () {
      Object.keys(this.form).forEach(field => {
        this.form[field] = field.slice(0, 5) === "files" ? null : ""
      })

      this.isDisabledAddressStreet = true
      this.isDisabledAddressCity = true
      this.isDisabledAddressState = true
      this.isEditFormShow = false
    },

    clearInvalidField (field) {
      this.invalidField[field] = false
    },

    clearFieldsAddress () {
      this.form.street = ""
      this.form.state = ""
      this.form.city = ""
    },

    clearInvalidlFields () {
      Object.keys(this.invalidField).forEach(field => {
        this.invalidField[field] = false
      })
    },

    async getBanks () {
      const response = await this.axios.get(
        `${this.filooServer}payment/banks`,
        { headers: { Authorization: this.$session.get("bearer") } }
      )

      if (response.status === 200) {
        this.banksList = response.data
      }
    },

    async getAccountDetails () {
      const response = await this.axios.get(
        `${this.$store.state.filooServer}payment/account`,
        { headers: { Authorization: this.$session.get("bearer") } }
      )
        if (response.status === 200) {
        this.financialData = response.data
        this.form.status = this.financialData.status || ""
        this.fillInData (this.financialData)
        this.$vs.loading.close()
        }
      
    },

    async getWithdrawTax () {
      const response = await this.axios.get(
        `${this.$store.state.filooServer}payment/account/checkTax`,
        { headers: { Authorization: this.$session.get("bearer") } }
      )

      if (response.status === 200 && !!response.data) {
        const taxResponse = await this.axios.get(
          `${this.$store.state.filooServer}parameters/tax/doctor_withdraw_tax`,
          { headers: { Authorization: this.$session.get("bearer") } }
        )

        this.withdrawTax = taxResponse.status === 200 ? taxResponse.data : 0
      }
    },

    fillInData (financialData) {
      if (financialData.details) {
        const { details } = financialData
        const agency = details.bank_agency.split("-")
        const account = details.bank_account.split("-")

        this.form.bankAgency = agency[0] || ""
        this.form.bankAgencyDigit = agency[1] || ""
        this.form.bankAccount = account[0] || ""
        this.form.bankAccountDigit = account[1] || ""

        if (details.bank) {
          const foundBank = this.banksList.find(bank => bank.name === details.bank)
          if (foundBank) {
            this.form.bank = foundBank
            this.form.bank.qtyCharAgency = parseInt(this.form.bank.qtyCharAgency || 0)
            this.form.bank.qtyCharAccount = this.isBankOperationRequired
              ? parseInt(this.form.bank.qtyCharAccount || 0) - 3
              : parseInt(this.form.bank.qtyCharAccount || 0)
          }
        }

        if (parseInt(account[0]) && this.isBankOperationRequired) {
          this.form.bankAccount = account[0].slice(3)
          this.form.bankOperation = this.bankOperations.find(op => op.code === account[0].slice(0, 3))
        }

        this.form.isLegalPerson = details.is_legal_person || ""
        this.form.responsibleCpf = details.cpf || ""
        this.form.companyCnpj = details.cnpj || ""
        this.form.companyName = details.company_name || ""
        this.form.responsibleName = details.resp_name || this.form.responsibleName

        this.form.zipcode = details.cep || ""
        this.form.street = details.address || ""
        this.form.city = details.city || ""
        this.form.state = details.state || ""
      }
      this.balanceAvaliableForWithdraw = financialData.balance_avaliable_for_withdraw || 0
      this.form.disabledButtonRescueBalance = financialData.blocked_by_admin
      this.form.isVerifiedAccount = this.computedIsVerifiedAccount
    },

    async getPersonalData () {
      const response = await this.axios.get(
        `${this.filooServer}doctor/personal-data`,
        { headers: { Authorization: this.$session.get("bearer") } }
      )

      if (response.status === 200) {
        this.responsibleName = response.data.name
        this.responsibleCpf = response.data.cpf
      }
    },

    async handleClickAddFile (evt, type) {
      if (evt.target.files[0]) {
        const config = this.filesConfig[type]
        const field = config.name
        const reader = await new FileReader()
        const file = evt.target.files[0]

        reader.readAsDataURL(file)
        reader.onloadend = async () => {
          this.invalidField[field] = false

          const chkExt = config.extensions
            ? await FilesHelper.checkExtension(file, config.extensions)
            : true
          const chkSize = config.size && !await FilesHelper.checkExtension(file, "pdf")
            ? await FilesHelper.checkSize(file, config.size)
            : true
          const chkDimensions = (config.minRes || config.maxRes) && !await FilesHelper.checkExtension(file, "pdf")
            ? await FilesHelper.checkDimensions(reader.result, config.minRes, config.maxRes)
            : true

          if (chkExt && chkSize && chkDimensions) {
            this.form[field] = file
          } else {
            let text = ""

            this.form[field] = null
            this.invalidField[field] = true

            if (!chkExt) {
              text = `Formato do arquivo incorreto, utilizar os formatos ${config.extensions.join(", ")}.`
            } else if (!chkSize) {
              text = "Tamanho do arquivo excedido, o arquivo deve ter no máximo 10MB"
            } else if (!chkDimensions) {
              text = "Dimensões do arquivo inválidas, o arquivo deve ter no "
              text = config.minRes ? `${text} mínimo ${config.minRes}x${config.minRes}` : text
              text = config.maxRes ? `${text}${config.minRes ? " e" : ""} máximo ${config.maxRes}x${config.maxRes}.` : text
            }

            this.$vs.notify({
              title: "Atenção!",
              text,
              color: "warning",
              icon: "error",
              position: "top-right",
              time: 6000,
            })
          }
        }
      }
    },

    async handleClickAccountEdit () {
      await this.fillInData(this.financialData)

      this.isEditFormShow = true
    },

    handleClickValidateFields () {
      this.invalidField = {
        ...this.invalidField,
        zipcode: !this.form.zipcode,
        street: !this.form.street,
        city: !this.form.city,
        state: !this.form.state,
      }

      if (!this.form.bank || !this.form.bank.name) {
        this.invalidField.bank = true
      }

      if (!this.form.bankAgency || this.form.bankAgency.length > this.form.bank.qtyCharAgency) {
        this.invalidField.bankAgency = true
      }

      if (needsAgDigit.includes(this.form.bank.name) && !this.form.bankAgencyDigit) {
        this.invalidField.bankAgencyDigit = true
      }

      if (!this.form.bankAccount || this.form.bankAccount.length < this.form.bank.qtyCharAccount) {
        this.invalidField.bankAccount = true
      }

      if (!unneedsAcDigit.includes(this.form.bank.name) && !this.form.bankAccountDigit) {
        this.invalidField.bankAccountDigit = true
      }

      if (!this.form.bankOperation && needsOpCode.includes(this.form.bank.name)) {
        this.invalidField.bankOperation = true
      }

      if (this.form.isLegalPerson) {
        this.invalidField.responsibleName = !this.form.responsibleName || !this.testFullName(this.form.responsibleName)

        this.invalidField.responsibleCpf = !this.form.responsibleCpf || !this.validateCPF(this.form.responsibleCpf)

        this.invalidField.companyCnpj = !this.form.companyCnpj || !this.validateCNPJ(this.form.companyCnpj)

        this.invalidField.companyName = !this.form.companyName

        this.invalidField.socialContract = !this.form.socialContract
        this.invalidField.balanceSheet = !this.form.balanceSheet
      }

      if (!this.form.filesSelfie) {
        this.invalidField.filesSelfie = true
      }

      if (!this.form.filesAddress) {
        this.invalidField.filesAddress = true
      }

      if (!this.form.filesIdentification) {
        this.invalidField.filesIdentification = true
      }

      if (Object.values(this.invalidField).filter(field => field).length) {
        this.showNotifyWarning(
          "Campos obrigatórios não preenchidos ou inválidos, preencha os campos corretamente e repita a operação.",
          6000
        )
      } else {
        this.$bvModal.show("balance-modal-confirm-change")
      }
    },

    handleClickAccountSave () {
      this.$vs.loading()

      const payload = new FormData()

      payload.append("createPaymentAccountDTO", JSON.stringify(this.computedFinancialDetails))
      payload.append("selfie", this.form.filesSelfie)
      payload.append("addressProof", this.form.filesAddress)
      payload.append("identification", this.form.filesIdentification)
      payload.append("socialContract", this.form.socialContract)
      payload.append("balanceSheet", this.form.balanceSheet)

      this.axios.post(
        `${this.filooServer}payment/account`,
        payload,
        { headers: { Authorization: this.$session.get("bearer") } }
      )
        .then((response) => {
          if (response.status === 201 || response.status === 200) {
            this.isEditFormShow = false
            this.showNotifySuccess("Operação realizada", "Dados bancários foram atualizados com sucesso", 3000)
            this.resetBalanceForm()
            this.init()
          }
        })
        .catch(() => {
          this.$swal({
            title: "Atenção!",
            text: "Não foi possível realizar esta operação. Revise seus dados bancários e tente novamente. Caso o problema persista contacte o administrador do sistema",
            icon: "error",
          })
        })
        .finally(() => {
          this.$vs.loading.close()
          this.$bvModal.hide("balance-modal-confirm-change")
        })
    },

    handleClickBtnCancel () {
      this.resetBalanceForm()
      this.form.filesSelfie = null
      this.form.filesAddress = null
      this.form.filesIdentification = null

      this.isEditFormShow = false
    },

    handleClickRescueBalance () {
      this.$bvModal.show("balance-modal-rescue-balance")
      this.balance.toWithdraw = 0
    },

    handleClickWithdraw () {
      if (this.balance.toWithdraw < 5) {
        this.$vs.notify({
          title: "Atenção",
          text: "Valor mínimo permitido é R$5,00.",
          color: "warning",
          position: "top-right",
        })
      } else if (this.financialData.balance_avaliable_for_withdraw < (this.balance.toWithdraw + this.withdrawTax)) {
        this.$vs.notify({
          title: "Atenção",
          text: `Seu saldo atual de ${this.computedBalanceAvailable} é menor do que o
            valor solicitado de ${this.maskMoney(this.balance.toWithdraw)}
            mais a taxa de saque de " ${this.maskMoney(this.withdrawTax)}.`,
          color: "warning",
          position: "top-right",
        })
        this.clearButtonLoading()
      } else {
        this.axios.post(
          this.$store.state.filooServer + "payment/account/withdraw",
          { amount: this.balance.toWithdraw },
          { headers: { Authorization: this.$session.get("bearer") } },
        ).then((response) => {
             if (response.status === 200) {
          this.$swal({
            title: "Seu resgate foi realizado com sucesso!",
            text: "Você pode visualizar suas transações no seu extrato financeiro",
            icon: "success",
          })
          this.init()
        }
        })
          .catch(() => {
            this.$swal({
              title: "Atenção!",
              text: "Não foi possível realizar esta operação. Revise seus dados bancários, verifique se possui saldo disponível e tente novamente. Caso o problema persista contacte o administrador do sistema",
              icon: "error",
            })
          })
          .finally(() => this.$bvModal.hide("balance-modal-rescue-balance"))   
      }
    },

    async searchDataAddress () {
      this.clearFieldsAddress()

      const zipcode = await this.getOnlyNumbers(this.form.zipcode)
      let isZipcodeValid = zipcode.length === 8

      if (isZipcodeValid) {
        const response = await this.getAddressByZipcode(this.form.zipcode)
        isZipcodeValid = response && response.address

        if (isZipcodeValid) {
          this.invalidField.zipcode = false
          this.form.street = response.address.street
          this.form.state = response.address.state
          this.form.city = response.address.city
        }

        this.invalidField.street = !this.form.street
        this.invalidField.city = !this.form.city
        this.invalidField.state = !this.form.state

        this.isDisabledAddressStreet = !!this.form.street
        this.isDisabledAddressCity = !!this.form.city
        this.isDisabledAddressState = !!this.form.state
      }
      if (zipcode && !isZipcodeValid) {
        await this.clearFieldsAddress()
        this.invalidField.zipcode = true
        this.invalidField.city = true
        this.invalidField.street = true
        this.invalidField.state = true

        this.showNotifyWarning(
          `Não foi possível localizar o CEP digitado, forneça um novo cep para
          dar sequência ao cadastro dos dados bancários.`,
          6000
        )
      }
    },
  },
}
</script>

<style lang="scss" src="./Balance.scss" scoped />
